import React from 'react';
import MuiToggleButton from '@mui/material/ToggleButton';
import type {ToggleButtonProps as MuiToggleButtonProps} from '@mui/material/ToggleButton';
import {styled} from '@mui/material/styles';
import {composeEventHandlers} from '../utils/composeEventHandlers';
import {excludeComponentProps} from '../utils/excludeComponentProps';

type BaseToggleButtonProps = Omit<MuiToggleButtonProps, 'color' | 'centerRipple' | 'disableRipple' | 'disableTouchRipple' | 'focusRipple' | 'TouchRippleProps' | 'disableFocusRipple' | 'children'>;

export interface ToggleButtonProps extends BaseToggleButtonProps {
    /**
     * Text to be placed
     * in Toggle Button
     * when selected
     */
    selectedText?: string;
    /**
     * Text to be placed
     * in Toggle Button
     */
    text?: string;
    /**
     * Icon to be placed
     * in Toggle Button
     */
    icon?: React.ReactNode;
    /**
     * Icon to be placed
     * in Toggle Button
     * when selected
     */
    selectedIcon?: React.ReactNode;
    /**
     * Centered content within the toggle button. Mainly used on vertically oriented buttons.
     */
    isCentered?: boolean;
}

const StyledToggleButton = styled(MuiToggleButton, {
    ...excludeComponentProps('selectedText', 'text', 'icon', 'isCentered', 'selectedIcon')
})<ToggleButtonProps>(({
    theme, size: currentSize, icon, text, isCentered
}) => {
    const size = currentSize && ['small', 'medium', 'large'].includes(currentSize) ? currentSize : 'medium';

    return {
        justifyContent: 'left',
        border: `1px solid ${theme.tokens.FuelColorBorderBase}`,
        lineHeight: theme.tokens.FuelLineHeightSm,
        backgroundColor: theme.tokens.FuelColorBackgroundActionDefaultBase,
        color: theme.tokens.FuelColorGray900,

        ...isCentered && {
            justifyContent: 'center'
        },

        '&:hover': {
            backgroundColor: theme.tokens.FuelColorBackgroundActionDefaultHovered,
            color: theme.tokens.FuelColorGray900
        },

        '&:active': {
            backgroundColor: theme.tokens.FuelColorBackgroundActionDefaultPressed,
            color: theme.tokens.FuelColorGray900
        },

        '&.MuiToggleButtonGroup-groupedHorizontal:not(:first-of-type)': {
            borderLeftColor: theme.tokens.FuelColorBorderBase // override
        },

        '&.MuiToggleButtonGroup-groupedVertical:not(:first-of-type)': {
            '&:not(:first-of-type)': {
                borderTopColor: theme.tokens.FuelColorBorderBase // override
            }
        },

        ...Boolean(icon) && Boolean(text) && {
            '& .MuiSvgIcon-root': {
                marginRight: theme.tokens.FuelSpacing1
            }
        },

        '&.Mui-selected': {
            backgroundColor: theme.tokens.FuelColorBackgroundActionDefaultSelected,
            borderColor: theme.tokens.FuelColorBorderSelected,
            color: theme.tokens.FuelColorTextActionTertiary,

            '&:hover': {
                backgroundColor: theme.tokens.FuelColorBackgroundActionDefaultSelected,
                color: theme.tokens.FuelColorTextActionTertiary
            },

            '&:focus-visible': {
                color: theme.tokens.FuelColorTextActionTertiary
            },

            '&.MuiToggleButtonGroup-grouped': {
                zIndex: 100
            },

            '&.MuiToggleButtonGroup-groupedHorizontal:not(:first-of-type)': {
                borderLeftColor: theme.tokens.FuelColorBorderSelected // override
            },

            '&.MuiToggleButtonGroup-groupedVertical:not(:first-of-type)': {
                borderTopColor: theme.tokens.FuelColorBorderSelected // override
            }
        },

        ...size === 'small' && {
            minHeight: '2rem',
            fontSize: theme.tokens.FuelFontSizeSm,
            padding: `${theme.tokens.FuelSpacing1} ${theme.tokens.FuelSpacing3}`
        },

        ...size === 'medium' && {
            minHeight: '2.25rem',
            fontSize: theme.tokens.FuelFontSizeSm,
            padding: `0.375rem ${theme.tokens.FuelSpacing4}`
        },

        ...size === 'large' && {
            minHeight: '2.5rem',
            fontSize: theme.tokens.FuelFontSizeMd,
            padding: `${theme.tokens.FuelSpacing2} ${theme.tokens.FuelSpacing6}`
        },

        '&.Mui-focusVisible': {
            backgroundColor: theme.tokens.FuelColorBackgroundActionDefaultHovered,
            color: theme.tokens.FuelColorGray900
        }
    };
});

export const ToggleButton = React.forwardRef<any, ToggleButtonProps>(({
    icon, onClick, selectedIcon, isCentered, selectedText, selected, text, ...props
}, ref) => {
    const [toggleText, setToggleText] = React.useState(text);
    const [toggleIcon, setToggleIcon] = React.useState(icon);

    const handleToggle = (): void => {
        if (selected) {
            setToggleText(text);
            setToggleIcon(icon);
        } else {
            const textValue = selectedText ? selectedText : text;
            const iconValue = selectedIcon ? selectedIcon : icon;

            setToggleText(textValue);
            setToggleIcon(iconValue);
        }
    };

    const onClickEvents = composeEventHandlers(handleToggle, onClick);

    return (
        <StyledToggleButton
            {...props}
            icon={icon}
            isCentered={isCentered}
            onClick={onClickEvents}
            ref={ref}
            selected={selected}
            text={text}
        >
            {toggleIcon}
            {toggleText}
        </StyledToggleButton>
    );
});

ToggleButton.displayName = 'ToggleButton';
